"use client";

import type { ButtonProps } from "./Button";
import type { ILinkProps } from "./Link";
import type { EventName } from "@mw/shared/analyticsEventNames";
import type { FormattedCTA } from "@utilities/payload";

import { captureEvent } from "@/app/_analytics/analytics";
import { AuthLink } from "./AuthDialog/AuthLink";
import { Button } from "./Button";
import { Link } from "./Link";
import { LinkTracked } from "./LinkTracked";
import { ModalVideoPlayer } from "./ModalVideoPlayer";

export type CTAButtonProps = FormattedCTA &
  Omit<ILinkProps, "appearance" | "ariaLabel" | "asButton" | "href" | "label" | "onClick" | "type"> & {
    appearance?: ButtonProps["variant"] | ILinkProps["appearance"] | null;
    ariaLabel?: string | null;
    href?: ILinkProps["href"];
    label?: ILinkProps["label"];
    eventName?: EventName;
    eventProperties?: Record<string, unknown>;
  };

export const CTAButton = ({
  appearance,
  ariaLabel,
  buttonVariants,
  children,
  className,
  description,
  disabled,
  eventName,
  eventProperties,
  href,
  icon,
  iconPosition,
  iconSource,
  img,
  label,
  rel,
  showExternalLoading,
  target,
  variant,
  ...props
}: CTAButtonProps) => {
  const resolvedLabel = label?.trim() || undefined;
  const resolvedButtonVariants = {
    ...buttonVariants,
    size: buttonVariants?.size ?? "lg",
    variant: buttonVariants?.variant ?? appearance,
  };

  switch (props.action) {
    case "button": {
      const content = resolvedLabel || children || props.button.label;

      if (props.button.type === "authDialog") {
        return (
          <AuthLink
            href={props.button.redirectTo || "/home"}
            initialView={props.button.tab === "sign-in" ? "sign-in" : "sign-up"}
            className={className}
            disabled={disabled}
            icon={icon}
            iconPosition={iconPosition}
            ariaLabel={ariaLabel ?? props.button.ariaLabel}
            buttonVariants={resolvedButtonVariants}
            onClick={
              eventName ? () => captureEvent(eventName, eventProperties, { transport: "sendBeacon" }) : undefined
            }
          >
            {content}
          </AuthLink>
        );
      }

      const videoButton = props.button as Extract<typeof props.button, { type: "videoPopup" }>;
      return (
        <ModalVideoPlayer
          customTrigger={
            <Button
              aria-label={ariaLabel ?? videoButton.ariaLabel}
              className={className}
              disabled={disabled}
              icon={icon}
              iconPosition={iconPosition}
              onClick={
                eventName ? () => captureEvent(eventName, eventProperties, { transport: "sendBeacon" }) : undefined
              }
              {...resolvedButtonVariants}
            >
              {content}
            </Button>
          }
          label={videoButton.label}
          video={videoButton.video}
        />
      );
    }

    case "link": {
      const { action: _action, link, ...linkPassthroughProps } = props;
      if (!link) {
        return null;
      }

      const content = resolvedLabel || children || link.label;
      const linkProps = {
        ...linkPassthroughProps,
        ariaLabel: ariaLabel ?? link.ariaLabel,
        asButton: true,
        buttonVariants: resolvedButtonVariants,
        className,
        description,
        disabled,
        href: href ?? link.href,
        icon,
        iconPosition,
        iconSource: iconSource ?? link.iconSource,
        img: img ?? link.img,
        label: resolvedLabel ?? link.label,
        rel: rel ?? link.rel,
        showExternalLoading,
        target: target ?? link.target,
        variant,
      };

      if (eventName) {
        return (
          <LinkTracked {...linkProps} eventName={eventName} eventProperties={eventProperties}>
            {content}
          </LinkTracked>
        );
      }

      return <Link {...linkProps}>{content}</Link>;
    }

    default: {
      const _: never = props;
      return null;
    }
  }
};
